import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import withStyles from '@mui/styles/withStyles';
import {
  TextField,
  TextareaAutosize,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';

import { convertToTitle } from '../../utils/formatting';
import ModifyNestedObjectFieldsStyles from './styles';

/**
 * ModifyNestedObjectFields Component
 *
 * Provides a JSON editor for MongoDB Mixed type fields in the Company schema.
 * Supports deeply nested object structures like:
 * - routing (with group_models, default_routing, model_routes)
 * - reportingDashboards
 * - sentenceFilters
 *
 * Features:
 * - Pretty-printed JSON with 2-space indentation
 * - Real-time validation with 500ms debounce
 * - Circular reference detection
 * - Proper error messages for invalid structures
 */
const ModifyNestedObjectFields = ({
  classes,
  label,
  initialValue,
  fieldName,
  updateFieldData,
  addUpdatedFieldName = (f) => f,
  setErrors = (f) => f,
  errors = {},
  setContainsError = (f) => f,
}) => {
  // Store each top-level key as a separate entry
  const [topLevelKeys, setTopLevelKeys] = useState(() => {
    const obj = initialValue || {};
    return Object.keys(obj).reduce((acc, key) => {
      acc[key] = JSON.stringify(obj[key], null, 2);
      return acc;
    }, {});
  });

  const [expandedKeys, setExpandedKeys] = useState({});
  const lastInitialValueRef = useRef(initialValue);

  const handleUpdateTopLevelKey = (key, newValue) => {
    setTopLevelKeys((prev) => ({
      ...prev,
      [key]: newValue,
    }));
    addUpdatedFieldName(fieldName);
  };

  const handleDeleteKey = (key) => {
    const newKeys = { ...topLevelKeys };
    delete newKeys[key];
    setTopLevelKeys(newKeys);
    addUpdatedFieldName(fieldName);

    // Clear error for this key if it exists
    const keyErrorField = `${fieldName}.${key}`;
    if (errors[keyErrorField]) {
      setErrors((errs) => {
        const newErrs = { ...errs };
        delete newErrs[keyErrorField];
        return newErrs;
      });
    }
  };

  const handleToggleExpand = (key) => {
    setExpandedKeys((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const isValidSubmission = (key, val) => {
    let isValid = true;
    let errorMessage = false;
    const keyErrorField = `${fieldName}.${key}`;

    try {
      const parsed = JSON.parse(val);

      // For top-level keys, we accept any valid JSON (objects, arrays, primitives, etc.)
      // MongoDB Mixed type is flexible

      // Check for circular references (will throw in JSON.stringify)
      if (typeof parsed === 'object' && parsed !== null) {
        try {
          JSON.stringify(parsed);
          errorMessage = false;
        } catch (err) {
          errorMessage = 'Circular reference detected';
          isValid = false;
        }
      }
    } catch (e) {
      // show error when invalid JSON
      errorMessage = e.message.includes('JSON')
        ? 'Invalid JSON syntax'
        : `JSON error: ${e.message}`;
      isValid = false;
    }

    setErrors((errs) => ({
      ...errs,
      [keyErrorField]: errorMessage,
    }));

    return isValid;
  };

  const validateAllKeys = () => {
    let allValid = true;
    Object.entries(topLevelKeys).forEach(([key, value]) => {
      if (!isValidSubmission(key, value)) {
        allValid = false;
      }
    });
    setContainsError(!allValid);
    return allValid;
  };

  useEffect(() => {
    const valueChangedTimer = setTimeout(() => {
      if (validateAllKeys()) {
        // Reconstruct the full object from top-level keys
        const reconstructedObj = Object.entries(topLevelKeys).reduce(
          (acc, [key, value]) => {
            try {
              acc[key] = JSON.parse(value);
            } catch (e) {
              // Skip invalid entries
            }
            return acc;
          },
          {}
        );
        updateFieldData(reconstructedObj, fieldName);
      }
    }, 500);
    return () => {
      clearTimeout(valueChangedTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topLevelKeys]);

  // Update internal state when initialValue changes (e.g., on data reload)
  // Only update if initialValue reference actually changed (external update, not our own change)
  useEffect(() => {
    // Skip if this is the same reference we've already processed
    if (lastInitialValueRef.current === initialValue) {
      return;
    }

    // Check if this is actually a different value by comparing stringified versions
    const lastStringified = JSON.stringify(lastInitialValueRef.current);
    const currentStringified = JSON.stringify(initialValue);

    if (lastStringified === currentStringified) {
      // Same content, just update the ref
      lastInitialValueRef.current = initialValue;
      return;
    }

    // Different content - update state
    const obj = initialValue || {};
    const newKeys = Object.keys(obj).reduce((acc, key) => {
      acc[key] = JSON.stringify(obj[key], null, 2);
      return acc;
    }, {});

    setTopLevelKeys(newKeys);
    lastInitialValueRef.current = initialValue;
  }, [initialValue]);

  return (
    <Box key={`modify-nested-obj-fields-${label}`} className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="h6" className={classes.label}>
          {label}
        </Typography>
        <Typography variant="caption" className={classes.helperText}>
          MongoDB Mixed type: Edit each top-level key separately
        </Typography>
      </Box>

      {/* Display each top-level key in an accordion */}
      <Box className={classes.keysContainer}>
        {Object.entries(topLevelKeys)
          .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
          .map(([key, value]) => (
            <Accordion
              key={`nested-field-${key}`}
              expanded={expandedKeys[key] || false}
              onChange={() => handleToggleExpand(key)}
              className={classes.accordion}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                className={classes.accordionSummary}
              >
                <Box className={classes.summaryContent}>
                  <Typography variant="subtitle1" className={classes.keyName}>
                    {convertToTitle(key)}
                  </Typography>
                  <Tooltip title="Delete this key">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteKey(key);
                      }}
                      className={classes.deleteButton}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </AccordionSummary>
              <AccordionDetails className={classes.accordionDetails}>
                <TextField
                  fullWidth
                  multiline
                  variant="outlined"
                  value={value}
                  onChange={(e) => handleUpdateTopLevelKey(key, e.target.value)}
                  error={!!errors[`${fieldName}.${key}`]}
                  helperText={
                    errors[`${fieldName}.${key}`] || `Edit ${key} JSON structure`
                  }
                  InputProps={{
                    inputComponent: TextareaAutosize,
                    inputProps: {
                      minRows: 4,
                      maxRows: 15,
                      style: {
                        fontFamily: 'monospace',
                        fontSize: '13px',
                      },
                    },
                  }}
                />
              </AccordionDetails>
            </Accordion>
          ))}
      </Box>

      {Object.keys(topLevelKeys).length === 0 && (
        <Box className={classes.emptyState}>
          <Typography variant="body2" color="textSecondary">
            No keys defined. Add a new key to get started.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

ModifyNestedObjectFields.propTypes = {
  classes: PropTypes.object.isRequired,
  initialValue: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  fieldName: PropTypes.string.isRequired,
  updateFieldData: PropTypes.func.isRequired,
  addUpdatedFieldName: PropTypes.func,
  setErrors: PropTypes.func,
  errors: PropTypes.object,
  setContainsError: PropTypes.func,
};

const StyledModifyNestedObjectFields = withStyles(ModifyNestedObjectFieldsStyles)(
  ModifyNestedObjectFields
);
StyledModifyNestedObjectFields.displayName = 'ModifyNestedObjectFields';

export default StyledModifyNestedObjectFields;
